/**
 * The mutation outbox (design §2.12).
 *
 * "Toast + retry for mutation failures; **the outbox retains the payload so
 * nothing typed is lost**." That second clause is the requirement — the toast is
 * only how the user learns about it. A founder who typed forty words of context
 * about someone they met ninety seconds ago must not lose them because the
 * office wifi dropped.
 *
 * Two properties make a replay safe:
 *
 *  1. The entry stores the `idempotencyKey` minted for the FIRST attempt, and a
 *     retry re-sends it. `@crm/api-client` passes it through as `Idempotency-Key`,
 *     and the server collapses `(tenant_id, key)` for 24h — so a request that
 *     actually succeeded but whose response was lost cannot create a second
 *     contact.
 *  2. The payload is stored verbatim, so a retry sends exactly what the user
 *     typed rather than whatever the form currently holds.
 *
 * ## Storage: IndexedDB, with an in-memory mirror in front of it
 *
 * The design specifies IndexedDB, and this is now IndexedDB. But IndexedDB is
 * asynchronous and `listOutbox()` is called from render, so the durable store
 * sits BEHIND a synchronous in-memory mirror:
 *
 *   read   → the mirror, always, synchronously
 *   write  → the mirror first (so the very next render is correct and the toast's
 *            promise is true when it is read), then persisted asynchronously
 *
 * That ordering is deliberate. Awaiting the write before showing the toast would
 * mean a slow disk could show "nothing was lost" a beat late, or not at all; and
 * a mirror updated only after the write resolves would make `enqueueOutbox` a
 * promise every call site would have to thread. The mirror IS the read model.
 *
 * `hydrateOutbox()` fills the mirror at boot. Until it resolves the mirror is
 * empty, which is honest: entries from a previous session are not yet known, and
 * the UI shows nothing rather than a stale count.
 *
 * ## The localStorage that used to be here
 *
 * v1 stored the queue in `localStorage` under `crm.outbox.v1`. `hydrateOutbox`
 * MIGRATES that key on first run and deletes it, so an upgrade does not strand
 * unsent work in a store nothing reads any more. `localStorage` also remains the
 * fallback when IndexedDB is unavailable (Safari private mode, blocked site
 * data) — and when neither works, the mirror alone still serves this tab, which
 * is enough for the toast's Retry button. A failing outbox must degrade; it must
 * never take down the mutation it was trying to protect.
 */

import { idbDel, idbGet, idbSet } from './idb.ts';

const IDB_KEY = 'outbox.v2';
/** The v1 home. Read once by `hydrateOutbox`, then removed. */
const LEGACY_STORAGE_KEY = 'crm.outbox.v1';
/** Where the fallback writes when IndexedDB is unusable. */
const FALLBACK_STORAGE_KEY = 'crm.outbox.v2';
const MAX_ENTRIES = 50;

export type OutboxEntry = {
  readonly id: string;
  /** Procedure path, e.g. `contacts.create`. Used to route a replay. */
  readonly operation: string;
  /** The exact input the failed call carried. */
  readonly payload: unknown;
  /** Reused on every replay so the server can collapse duplicates. */
  readonly idempotencyKey: string;
  readonly correlationId: string;
  readonly createdAt: string;
  readonly attempts: number;
  readonly lastError: string;
  /** One line of human context for the toast: "Priya Raman". */
  readonly label: string;
};

type Listener = (entries: readonly OutboxEntry[]) => void;

const listeners = new Set<Listener>();

/** The synchronous read model. Every `listOutbox()` answers from here. */
let mirror: readonly OutboxEntry[] = [];

/**
 * Set once IndexedDB has refused a write. After that the fallback is used
 * directly rather than paying an open+fail round trip on every mutation.
 */
let idbUsable = true;

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Blocked by a privacy setting; the caller degrades to in-memory only.
    return null;
  }
}

function isEntry(value: unknown): value is OutboxEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<OutboxEntry>;
  return (
    typeof entry.id === 'string' &&
    typeof entry.operation === 'string' &&
    typeof entry.idempotencyKey === 'string' &&
    typeof entry.createdAt === 'string'
  );
}

function sanitize(value: unknown): OutboxEntry[] {
  return Array.isArray(value) ? value.filter(isEntry).slice(0, MAX_ENTRIES) : [];
}

function readLocal(key: string): OutboxEntry[] {
  const store = storage();
  if (store === null) return [];
  try {
    const raw = store.getItem(key);
    if (raw === null) return [];
    return sanitize(JSON.parse(raw));
  } catch {
    // A corrupt outbox must not brick the app on boot. Drop it and move on.
    return [];
  }
}

function writeLocal(entries: readonly OutboxEntry[]): void {
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota or private mode: the mirror above still serves this tab.
  }
}

function removeLocal(key: string): void {
  const store = storage();
  if (store === null) return;
  try {
    store.removeItem(key);
  } catch {
    // Nothing to do; a stale key is inert once nothing reads it.
  }
}

function notify(): void {
  for (const listener of listeners) {
    listener(mirror);
  }
}

/**
 * Persists the mirror. Fire-and-forget by design — the caller has already
 * updated the read model and told the user.
 */
function persist(): void {
  const snapshot = mirror;
  if (!idbUsable) {
    writeLocal(snapshot);
    return;
  }
  void idbSet(IDB_KEY, snapshot).then((ok) => {
    if (ok) {
      // The durable copy landed, so the localStorage fallback is now a STALE
      // second source of truth. Left in place it would resurrect entries at the
      // next hydrate — including ones retried successfully in the meantime,
      // whose replay after the server's 24h idempotency window could create a
      // duplicate. One store speaks at a time.
      removeLocal(FALLBACK_STORAGE_KEY);
      return;
    }
    // One refusal is enough to stop trying: the failure modes here (no
    // IndexedDB, private mode, blocked site data) are all sticky for the tab.
    idbUsable = false;
    writeLocal(snapshot);
  });
}

/**
 * Loads persisted entries into the mirror, migrating the v1 `localStorage`
 * queue if one is still there. Call once, at boot.
 *
 * Entries queued *before* hydration finishes are not clobbered: the persisted
 * set is merged UNDER them, keyed on `idempotencyKey`, so a mutation that failed
 * during startup survives. Resolves to the number of entries in the mirror.
 */
export async function hydrateOutbox(): Promise<number> {
  const persisted = sanitize(await idbGet<unknown>(IDB_KEY));
  const legacy = readLocal(LEGACY_STORAGE_KEY);
  const fallback = readLocal(FALLBACK_STORAGE_KEY);

  const merged: OutboxEntry[] = [];
  const seen = new Set<string>();
  // Newest-first ordering is preserved by taking the in-session entries first.
  for (const entry of [...mirror, ...persisted, ...fallback, ...legacy]) {
    if (seen.has(entry.idempotencyKey)) continue;
    seen.add(entry.idempotencyKey);
    merged.push(entry);
  }

  mirror = merged.slice(0, MAX_ENTRIES);

  if (legacy.length > 0) {
    // Migrated: the v1 key is dropped so a later downgrade cannot resurrect
    // entries that have since been retried successfully.
    removeLocal(LEGACY_STORAGE_KEY);
  }
  if (mirror.length > 0 || legacy.length > 0 || fallback.length > 0) {
    persist();
  }
  notify();
  return mirror.length;
}

export function listOutbox(): readonly OutboxEntry[] {
  return mirror;
}

/** Adds an entry, replacing any earlier one with the same idempotency key. */
export function enqueueOutbox(entry: OutboxEntry): void {
  const rest = mirror.filter((existing) => existing.idempotencyKey !== entry.idempotencyKey);
  mirror = [entry, ...rest].slice(0, MAX_ENTRIES);
  notify();
  persist();
}

export function removeOutbox(id: string): void {
  const next = mirror.filter((entry) => entry.id !== id);
  if (next.length === mirror.length) return;
  mirror = next;
  notify();
  persist();
}

export function clearOutbox(): void {
  mirror = [];
  notify();
  // Unconditional: `idbUsable` flips false after ONE refused write, but a
  // durable copy written before the flip would outlive the clear and resurrect
  // at the next boot. `idbDel` resolves false harmlessly when the store really
  // is unreachable, so attempting it costs nothing and deletes what it can.
  void idbDel(IDB_KEY);
  removeLocal(FALLBACK_STORAGE_KEY);
}

export function subscribeOutbox(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** "1 unsent change" / "3 unsent changes" — the toast's honesty clause. */
export function outboxSummary(entries: readonly OutboxEntry[] = listOutbox()): string | null {
  if (entries.length === 0) return null;
  return entries.length === 1
    ? '1 unsent change is saved'
    : `${entries.length} unsent changes are saved`;
}

/**
 * Test seam. Drops the mirror and re-arms IndexedDB; it does NOT touch either
 * store, so a test that wants a clean disk clears it itself.
 */
export function resetOutboxMirrorForTests(): void {
  mirror = [];
  idbUsable = true;
}
